import { useEffect, useState } from "react"
import type { OpenCodeApi } from "../../lib/opencodeApi"
import type { Permission } from "../../lib/opencodeTypes"
import { startSingleFlightPoll } from "../../lib/singleFlightPoll"
import { Button } from "../ui"
import { AgentDialog } from "./AgentDialog"

interface PermissionDialogProps {
  api: OpenCodeApi
  sessionId: string
}

export function PermissionDialog({ api, sessionId }: PermissionDialogProps) {
  const [permissions, setPermissions] = useState<Permission[]>([])

  useEffect(() => {
    return startSingleFlightPoll(async (signal) => {
      try {
        setPermissions(await api.listPermissions(sessionId, signal))
      } catch (err) {
        if (!signal.aborted) console.error("Failed to load permissions:", err)
      }
    }, 2000)
  }, [api, sessionId])

  // Thrown rather than caught, so the card can show the failure next to the
  // button that caused it. Swallowed into console.error, an Approve that failed
  // was indistinguishable from one that did nothing.
  const handleReply = async (permissionId: string, approved: boolean) => {
    await api.replyToPermission(sessionId, permissionId, approved)
    setPermissions((prev) => prev.filter((p) => p.id !== permissionId))
  }

  if (permissions.length === 0) {
    return null
  }

  return (
    <AgentDialog title="Permission Required">
      <div className="space-y-4">
        {permissions.map((permission) => (
          <PermissionCard
            key={permission.id}
            permission={permission}
            onApprove={() => handleReply(permission.id, true)}
            onDeny={() => handleReply(permission.id, false)}
          />
        ))}
      </div>
    </AgentDialog>
  )
}

interface PermissionCardProps {
  permission: Permission
  onApprove: () => Promise<void>
  onDeny: () => Promise<void>
}

function PermissionCard({ permission, onApprove, onDeny }: PermissionCardProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (reply: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await reply()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to answer")
      setBusy(false)
    }
  }

  return (
    <div className="rounded border border-border bg-surface-muted p-4">
      <div className="mb-2">
        <div className="text-sm font-medium text-foreground">{permission.action}</div>
        {permission.resources.length > 0 && (
          <div className="mt-1 text-xs text-muted">
            Resources: {permission.resources.join(", ")}
          </div>
        )}
      </div>

      {error && <p className="mb-2 text-sm text-danger">{error}</p>}

      <div className="flex gap-2">
        <Button onClick={() => void run(onApprove)} variant="primary" disabled={busy}>
          {busy ? "Sending…" : "Approve"}
        </Button>
        <Button onClick={() => void run(onDeny)} variant="ghost" disabled={busy}>
          Deny
        </Button>
      </div>
    </div>
  )
}
